




import { Prop, Component, Vue, Watch, Ref } from 'vue-property-decorator'
import { Dashboard } from '@/api/model/dashboard'
import { DashboardAPI } from '@/api/backend-client'
import { AuthClient } from '@/api/auth-client'
import moment from 'moment'
import * as pbi from 'powerbi-client'

interface EmbedTokenResponse {
    dashboardId: string,
    token: string,
    tokenId: string,
    expiration: string,
}

@Component
export default class PowerBiReport extends Vue {
    @Prop({ required: true }) readonly dashboard!: Dashboard
    @Ref() readonly container!: HTMLDivElement
    embedToken: EmbedTokenResponse = null
    embedTokenRefreshTimeout: ReturnType<typeof setTimeout> = null

    powerbi = new pbi.service.Service(
        pbi.factories.hpmFactory,
        pbi.factories.wpmpFactory,
        pbi.factories.routerFactory,
    )

    async mounted(): Promise<void> {
        this.powerbi.bootstrap(this.container, {
            type: 'report',
            settings: {
                localeSettings: {
                    language: this.$i18n.locale,
                },
                bars: {
                    actionBar: { visible: true },
                    statusBar: { visible: true },
                },
            },
        })

        await this.fetchEmbedToken()
        await this.createDashboard()
    }

    async beforeDestroy(): Promise<void> {
        this.unscheduleEmbedTokenRefresh()
        await this.destroyDashboard()
    }

    async fetchEmbedToken(): Promise<void> {
        const client = new AuthClient().axiosClient
        const basePath = process.env.VUE_APP_KEYVUE_BACKEND_BASEPATH || ''
        const response = await client.post(`${basePath}/service/power_bi/embed_token`, {
            dashboardId: this.dashboard.id,
        })

        this.embedToken = response.data
    }

    @Watch('embedToken.token')
    updateEmbedToken(newToken: string|null, oldToken: string|null): void {
        if (!oldToken) {
            return
        }

        const report = this.powerbi.get(this.container)
        report.setAccessToken(this.embedToken.token)
    }

    @Watch('embedToken.expiration')
    scheduleEmbedTokenRefresh(): void {
        this.unscheduleEmbedTokenRefresh()

        const now = moment()
        const expiration = moment(this.embedToken.expiration)
        const expirationDuration = moment.duration(expiration.diff(now))

        this.embedTokenRefreshTimeout = setTimeout(this.fetchEmbedToken, expirationDuration.asMilliseconds() - (60 * 1000))
    }

    unscheduleEmbedTokenRefresh(): void {
        if (this.embedTokenRefreshTimeout) {
            clearTimeout(this.embedTokenRefreshTimeout)
        }
    }

    async createDashboard(): Promise<void> {
        this.powerbi.embed(this.container, {
            type: 'report',
            id: this.dashboard.powerBiId,
            tokenType: pbi.models.TokenType.Embed,
            accessToken: this.embedToken.token,
        })
    }

    async destroyDashboard(): Promise<void> {
        this.powerbi.reset(this.container)
    }
}
